/**
 * Subpage shell — shared HTML structure for content pages
 * Generates complete HTML document with header, footer, and body content
 */
import { esc } from '@/utils/escape';
import { generateFontLinksHTML } from '../fonts';
import { generateCookieHTML } from '../components/cookie';
import { resolveNavHref } from './nav-helper';

interface SubpageOpts {
  title: string;
  metaDesc: string;
  bodyContent: string;
}

export function generateSubpageShell(settings: any, components: any, opts: SubpageOpts): string {
  const title = opts.title || '';
  const metaDesc = opts.metaDesc || '';
  const bodyContent = opts.bodyContent || '';
  const headerData = (components && components.header && components.header.data) || {};
  const footerData = (components && components.footer && components.footer.data) || {};
  const preset = settings.stylePreset || 'signature';
  const density = settings.designDensity || 'balanced';

  // Build FX class list (consistent with page.js)
  const fxClasses: string[] = [];
  const intensity = settings.fxIntensity || 'medium';
  if (settings.fxOrbs) fxClasses.push('fx-orbs', 'fx-orbs-' + intensity);
  if (settings.fxGrain) fxClasses.push('fx-grain', 'fx-grain-' + intensity);
  if (settings.fxGlass) fxClasses.push('fx-glass-cards');
  const fxClassStr = fxClasses.length ? ' class="' + fxClasses.join(' ') + '"' : '';

  // Subpage header with links back to index
  const navItems = (headerData.navItems || [])
    .map(function(item: string) {
      const href = resolveNavHref(item, settings, true);
      return '        <a href="' + href + '" class="header__nav-link">' + esc(item) + '</a>';
    })
    .join('\n');

  const mobileNavItems = (headerData.navItems || [])
    .map(function(item: string) {
      const href = resolveNavHref(item, settings, true);
      return '        <a href="' + href + '" class="header__mobile-link">' + esc(item) + '</a>';
    })
    .join('\n');

  const headerHTML = '  <header class="header" id="header">\n' +
    '    <div class="header__inner container">\n' +
    '      <a href="index.html" class="header__brand">\n' +
    (headerData.logoUrl
      ? '        <img src="' + esc(headerData.logoUrl) + '" alt="' + esc(headerData.companyName || settings.siteName) + '" class="header__logo">\n'
      : '        <span class="header__logo">' + esc(headerData.logoText || 'BF') + '</span>\n') +
    '        <span class="header__name">' + esc(headerData.companyName || settings.siteName) + '</span>\n' +
    '      </a>\n' +
    '      <nav class="header__nav" id="main-nav">\n' +
    navItems + '\n' +
    (settings.darkModeToggle ? '        <button class="header__dark-toggle" id="dark-toggle" aria-label="Dark Mode umschalten"></button>\n' : '') +
    '        <a href="index.html#kontakt" class="header__cta btn btn--primary">' + esc(headerData.ctaText || 'Anfrage') + '</a>\n' +
    '      </nav>\n' +
    '      <button class="header__toggle" id="menu-toggle" aria-label="Men\u00fc \u00f6ffnen" aria-expanded="false">\n' +
    '        <span></span><span></span><span></span>\n' +
    '      </button>\n' +
    '    </div>\n' +
    '    <nav class="header__mobile-nav" id="mobile-nav" aria-hidden="true">\n' +
    mobileNavItems + '\n' +
    '      <a href="index.html#kontakt" class="btn btn--primary">' + esc(headerData.ctaText || 'Anfrage') + '</a>\n' +
    '    </nav>\n' +
    '  </header>';

  // Subpage footer with dynamic nav
  let footerNavLinks = '<li><a href="index.html">Startseite</a></li>\n';
  if (settings.generateAngebot) footerNavLinks += '              <li><a href="angebot.html">Angebot</a></li>\n';
  if (settings.generateMethoden) footerNavLinks += '              <li><a href="methoden.html">Methoden</a></li>\n';
  if (settings.generateUeberMich) footerNavLinks += '              <li><a href="ueber-mich.html">\u00dcber mich</a></li>\n';
  footerNavLinks += '              <li><a href="index.html#kontakt">Kontakt</a></li>';

  let footerLegalLinks = '';
  if (settings.generateImpressum) footerLegalLinks += '              <li><a href="impressum.html">Impressum</a></li>\n';
  if (settings.generateDatenschutz) footerLegalLinks += '              <li><a href="datenschutz.html">Datenschutz</a></li>\n';
  if (settings.generateAgb) footerLegalLinks += '              <li><a href="agb.html">AGB</a></li>\n';

  const footerHTML = '  <footer class="footer">\n' +
    '    <div class="container">\n' +
    '      <div class="footer__grid">\n' +
    '        <div class="footer__brand">\n' +
    '          <strong>' + esc(footerData.companyName || settings.siteName) + '</strong>\n' +
    (footerData.tagline ? '          <p>' + esc(footerData.tagline) + '</p>\n' : '') +
    '        </div>\n' +
    '        <div class="footer__column">\n' +
    '          <h4>Navigation</h4>\n' +
    '          <ul>\n' +
    '              ' + footerNavLinks + '\n' +
    '          </ul>\n' +
    '        </div>\n' +
    (footerLegalLinks ? '        <div class="footer__column">\n' +
    '          <h4>Rechtliches</h4>\n' +
    '          <ul>\n' +
    '              ' + footerLegalLinks + '\n' +
    '          </ul>\n' +
    '        </div>\n' : '') +
    '        <div class="footer__column">\n' +
    '          <h4>Kontakt</h4>\n' +
    (footerData.email ? '          <p>' + esc(footerData.email) + '</p>\n' : '') +
    (footerData.phone ? '          <p>' + esc(footerData.phone) + '</p>\n' : '') +
    (footerData.address ? '          <p>' + esc(footerData.address) + '</p>\n' : '') +
    '        </div>\n' +
    '      </div>\n' +
    '      <div class="footer__bottom">\n' +
    '        <p>\u00a9 ' + new Date().getFullYear() + ' ' + esc(footerData.companyName || settings.siteName) + '. Alle Rechte vorbehalten.</p>\n' +
    '      </div>\n' +
    '    </div>\n' +
    '  </footer>';

  return '<!DOCTYPE html>\n' +
    '<html lang="de"' + (settings.darkMode ? ' class="dark"' : '') + '>\n' +
    '<head>\n' +
    '  <meta charset="UTF-8">\n' +
    '  <meta name="viewport" content="width=device-width, initial-scale=1.0">\n' +
    '  <title>' + esc(title) + ' \u2013 ' + esc(settings.siteName) + '</title>\n' +
    '  <meta name="description" content="' + esc(metaDesc) + '">\n' +
    generateFontLinksHTML(settings) + '\n' +
    '  <link rel="stylesheet" href="css/tokens.css">\n' +
    '  <link rel="stylesheet" href="css/components.css">\n' +
    '</head>\n' +
    '<body data-style="' + esc(preset) + '" data-density="' + esc(density) + '"' + fxClassStr + '>\n' +
    (settings.fxGrain ? '  <div class="fx-grain-overlay" aria-hidden="true"></div>\n' : '') +
    '  <a href="#main" class="skip-link">Zum Inhalt springen</a>\n' +
    headerHTML + '\n' +
    '  <main id="main" class="content-page">\n' +
    '    <div class="container">\n' +
    bodyContent + '\n' +
    '    </div>\n' +
    '  </main>\n' +
    footerHTML + '\n' +
    (components && components.cookie && components.cookie.enabled ? generateCookieHTML(components.cookie.data, settings) + '\n' : '') +
    '  <script src="js/main.js"><' + '/script>\n' +
    '</body>\n' +
    '</html>';
}
